import React, { useState, useRef, useEffect } from "react";
import { MoreHorizontal, Pencil, Share2, Trash2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import { ShareConversationModal } from "./ShareConversationModal";
import { ShimmerText } from "./ShimmerText";
import { Conversation } from "./types";

interface ConversationItemProps {
  conversation: Conversation;
  isSelected: boolean;
  showCheckmark?: boolean;
  /** Whether this conversation has an active stream */
  isStreaming?: boolean;
  /** Unique ID for aria-activedescendant reference */
  id?: string;
  /** Whether this item currently has keyboard focus (for roving tabindex) */
  isFocused?: boolean;
  onSelect: (conversationId: string) => void;
  onDelete: (conversationId: string, event: React.MouseEvent) => void;
  onRename: (conversationId: string, displayName: string) => void;
  formatLabel: (conversation: Conversation) => string;
}

export function ConversationItem({
  conversation,
  isSelected,
  showCheckmark = false,
  isStreaming = false,
  id,
  isFocused = false,
  onSelect,
  onDelete,
  onRename,
  formatLabel,
}: ConversationItemProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editingName, setEditingName] = useState("");
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const label = formatLabel(conversation);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSelect = () => {
    if (!isEditing) {
      onSelect(conversation.id);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    onDelete(conversation.id, e);
  };

  const handleShareClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPopoverOpen(false);
    setShareModalOpen(true);
  };

  const handleRenameClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingName(label);
    setPopoverOpen(false);
    setIsEditing(true);
  };

  const handleSave = () => {
    const trimmedName = editingName.trim();
    if (trimmedName && trimmedName !== label) {
      onRename(conversation.id, trimmedName);
    }
    setIsEditing(false);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    // Stop propagation to prevent parent handlers from capturing keys (e.g., space for selection)
    e.stopPropagation();

    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setIsEditing(false);
    }
  };

  const handleOptionKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleSelect();
    }
  };

  const CheckmarkIcon = () => (
    <svg className="size-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );

  return (
    <div
      id={id}
      role="option"
      aria-selected={isSelected}
      aria-label={`Select conversation: ${label}`}
      tabIndex={isFocused ? 0 : -1}
      className={cn(
        "relative group flex items-center justify-between px-2 py-1.5 mb-0.5 text-[13px] cursor-pointer rounded",
        "aria-selected:bg-gray-200/70 aria-selected:hover:bg-gray-200/70 hover:bg-gray-100",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
        showCheckmark && isSelected && "bg-transparent aria-selected:bg-transparent"
      )}
      onClick={handleSelect}
      onKeyDown={handleOptionKeyDown}
    >
      <div className="flex items-center overflow-hidden flex-1 min-w-0">
        {showCheckmark && (
          <div className="mr-2 w-4 flex justify-center shrink-0">
            {isSelected ? <CheckmarkIcon /> : null}
          </div>
        )}
        {isEditing ? (
          <input
            ref={inputRef}
            type="text"
            value={editingName}
            onChange={(e) => setEditingName(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleInputKeyDown}
            onClick={(e) => e.stopPropagation()}
            className="w-full bg-white border border-gray-300 rounded px-1 py-0.5 text-[13px] outline-none focus:border-blue-500"
            aria-label="Rename conversation"
          />
        ) : isStreaming && !isSelected ? (
          <ShimmerText className="truncate">{label}</ShimmerText>
        ) : (
          <Tooltip delayDuration={900}>
            <TooltipTrigger asChild>
              <p className="truncate">{label}</p>
            </TooltipTrigger>
            <TooltipContent hideArrow sideOffset={8}>
              <div>{label}</div>
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      {!isEditing && (
        <div className="flex items-center ml-2 shrink-0">
          <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
            <PopoverTrigger asChild>
              <button
                className="opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100 transition-opacity p-1 hover:bg-gray-200 data-[state=open]:bg-gray-200 rounded cursor-pointer"
                onClick={(e) => e.stopPropagation()}
                aria-label="Conversation options"
              >
                <MoreHorizontal className="size-4 text-gray-500" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto min-w-[140px] p-1" align="start">
              <button
                className="flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                onClick={handleShareClick}
              >
                <Share2 className="size-4 shrink-0" />
                Share Conversation
              </button>
              <button
                className="flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                onClick={handleRenameClick}
              >
                <Pencil className="size-4 shrink-0" />
                Rename
              </button>
              <button
                className="flex items-center gap-2 w-full px-2 py-1.5 text-sm text-destructive hover:bg-destructive/10 rounded cursor-pointer whitespace-nowrap"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(e);
                }}
              >
                <Trash2 className="size-4 shrink-0" />
                Delete
              </button>
            </PopoverContent>
          </Popover>
        </div>
      )}

      <ShareConversationModal
        open={shareModalOpen}
        onOpenChange={setShareModalOpen}
        conversationId={conversation.id}
      />
    </div>
  );
}
